import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { HeaderComponent } from './components/header/header.component';
import {
  ExcelHelperService,
  GlobalStateService,
  UserContextPermissionRoleService,
} from './services';
import {
  ApplicationContext,
  getAllInputSheetTableNames,
  getInputSheetNames,
  getInputSheetNamesToProtect,
} from './model';
import { UserContextInitService } from './services/user-workpaper-context/user-workpaper-context.service';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { SaveWorksheetService } from './services/worksheet/save-worksheet.service';


@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  hasAccess = false;
  appContext?: ApplicationContext;
  isEntityLocked = false;
  isOnHomePage = true;

  private readonly routerSubscription = new Subscription();

  constructor(
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly excelHelperService: ExcelHelperService,
    private readonly globalStateService: GlobalStateService,
    private readonly userContextInitService: UserContextInitService,
    private readonly userContextPermissionRoleService: UserContextPermissionRoleService,
    private readonly saveWorksheetService: SaveWorksheetService,
  ) {
    this.trackCurrentRoute();
  }

  async ngOnInit(): Promise<void> {
    await this.initAppContext();
    if (!this.hasAccess) {
      this.router.navigate(['/unauthorized']);
    } else {
      this.router.navigate(['/home']);
    }
    console.log('[App] initial navigation complete — hasAccess:', this.hasAccess);
  }

  // Tracks the current route for isOnHomePage state.
  private trackCurrentRoute(): void {
    this.routerSubscription.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(e => {
          this.isOnHomePage = e.urlAfterRedirects === '/home';
        })
    );
  }

  // Handles Home button click from the header.
  onHomeClicked(): void {
    this.saveWorksheetService.clearMessage();
    this.router.navigate(['/home']);
  }

  // Initializes the application context and sets access info and workbook protection.
  private async initAppContext(): Promise<void> {
    try {
      this.appContext = this.globalStateService.getWorkpaperContextData();
      await this.setAccessInfo();
      await this.ensureWorkbookProtection();
    } catch (error) {
      this.handleError('Error during app initialization:', error);
    }
  }

  // Loads and sets user access information.
  private async setAccessInfo(): Promise<void> {
    if (!this.appContext) return;
    try {
      const result = await firstValueFrom(
        this.userContextPermissionRoleService.loadAccessInfo(this.appContext)
      );
      const accessInfo =
        this.userContextPermissionRoleService.hasAccess(result);
      this.hasAccess = accessInfo.hasAccess;
      this.isEntityLocked = accessInfo.isEntityLocked;
    } catch (error) {
      this.handleError('Error loading access info:', error);
    }
  }

  // Ensures workbook and sheets are protected and totals are enabled.
  private async ensureWorkbookProtection(): Promise<void> {
    if (!this.appContext) return;
    await this.ensureWorkpaperPassword();

    const password = this.appContext.workpaperPassword || '';    
    const sheetsInfo = getAllInputSheetTableNames();
    const sheets = getInputSheetNames();
    const sheetsToProtect = getInputSheetNamesToProtect();

    try {
      await this.excelHelperService.unprotectSheetsBulk(sheets, password);
      await this.excelHelperService.enableShowTotals(sheetsInfo);
      await this.excelHelperService.protectSheetsBulk(sheetsToProtect, password );
      console.log('sheets with totals enabled:', sheetsInfo);
    } catch (error) {
      this.handleError('Error while enabling totals:', error);
    } finally {
      const isProtected =
        await this.excelHelperService.getWorkbookProtectionState();
      if (!isProtected) await this.excelHelperService.protectWorkbook(password);
    }
  }

  // Ensures the workpaper password is set in the app context.
  private async ensureWorkpaperPassword(): Promise<void> {
    if (
      !this.appContext?.workpaperPassword ||
      this.appContext.workpaperPassword.length === 0
    ) {
      await this.userContextInitService.setPassword();
      this.appContext = this.globalStateService.getWorkpaperContextData();
    }
  }

  // Handles errors by logging them.
  private handleError(message: string, error: unknown): void {
    console.error(message, error);
  }

  signOut(): void {
    this.msalService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }
}
